import { type ReactNode, useState } from 'react';

import deleteIcon from '@/assets/images/delete.webp';

import { Modal } from '../modal';
import { OverlayPattern } from '../overlay';

import styles from './dialog.module.scss';

export type DeleteConfirmModalProps = {
  open: boolean;
  /** Confirm-state title, e.g. "Delete Component?". */
  title: ReactNode;
  /** Confirm-state body, e.g. "This component will be permanently deleted.". */
  body: ReactNode;
  /** Warning-state title; defaults to "Cannot be deleted". */
  blockedTitle?: ReactNode;
  /** Warning-state body explaining why the delete is blocked. */
  blockedBody: ReactNode;
  /** When set, the blocked state shows a single centred Close button (this label) instead of a footer-less warning. Omit to keep the header-× dismiss (catalog default). */
  blockedCloseText?: string;
  /** Danger CTA label; defaults to "Delete". */
  confirmText?: string;
  /**
   * Perform the deletion. Resolve `{ blocked: true }` to switch the modal to the
   * button-less warning state (keep it open); resolve `void` to close it. Handle
   * your own errors (e.g. toast) and resolve — a rejection leaves the modal open.
   */
  onConfirm: () => Promise<{ blocked: true } | void>;
  /** Dismiss the modal (× / Cancel / mask, and the success path). */
  onClose: () => void;
};

export function DeleteConfirmModal({
  open,
  title,
  body,
  blockedTitle = 'Cannot be deleted',
  blockedBody,
  blockedCloseText,
  confirmText = 'Delete',
  onConfirm,
  onClose,
}: DeleteConfirmModalProps) {
  const [blocked, setBlocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [wasOpen, setWasOpen] = useState(open);

  // Reset to the confirm state whenever the modal (re)opens. Returning null when closed
  // renders nothing but does NOT unmount the component, so useState survives across opens —
  // without this, a prior blocked (409) delete would leave every later open stuck on the
  // warning copy. Adjusting state during render is the React-recommended reset pattern.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setBlocked(false);
      setLoading(false);
    }
  }

  const handlePrimary = async () => {
    setLoading(true);
    try {
      const result = await onConfirm();
      if (result?.blocked) {
        setBlocked(true); // stay open, drop the footer, show the warning copy
      } else {
        onClose();
      }
    } finally {
      setLoading(false);
    }
  };

  // Render nothing when closed so the overlay leaves the DOM immediately (mirrors how
  // <DialogHost> renders <ConfirmDialog> conditionally). State is reset on reopen above,
  // since returning null keeps the component mounted.
  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      loading={loading}
      // Blocked: single centred neutral Close button when blockedCloseText is set, else no footer
      // (catalog dismiss via header × — Figma 5151-1250860 / 4464-1031740). Confirm: Cancel + red Delete.
      hideFooter={blocked && !blockedCloseText}
      hideCancel={blocked}
      primaryText={blocked ? blockedCloseText : confirmText}
      onPrimary={blocked ? onClose : () => void handlePrimary()}
      primaryDanger={!blocked}
      cancelText="Cancel"
      onCancel={onClose}
      footerAlign="center"
    >
      <div className={styles.layout}>
        <OverlayPattern className={styles.pattern} />
        <span className={styles.iconWrap}>
          <img src={deleteIcon} alt="" aria-hidden="true" className={styles.icon} />
        </span>
        <h2 className={styles.title}>{blocked ? blockedTitle : title}</h2>
        <p className={styles.subtitle}>{blocked ? blockedBody : body}</p>
      </div>
    </Modal>
  );
}
